import { type FC, createContext, useContext, useState } from "react";
import { ProductTabularViewActionCell } from "~/Search/components/ProductResults/views/TabularView/ProductTabularViewActionCell/ProductTabularViewActionCell";
import { LinkButton } from "../../../common/components/Link";
import { type TabularViewData } from "../../../Search/models";
import { type Row } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { VariantProductsTableModal } from "~/HousingSelectionAssistant/components/VariantProductsTableModal/VariantProductsTableModal";
import {
  PurchasePlaceProvider,
  PurchaseTypeProvider,
} from "~/feature/Analytics/ga/recommendations/RecommendationContexts";

interface Props {
  row: Row<TabularViewData>;
}

export const HousingProductTabularViewActionCell: FC<Props> = ({ row }: Props) => {
  const { product, variantProducts } = row.original;
  const { t } = useTranslation();
  const isExpandActionCell = variantProducts.length > 0;
  const [isOpen, setIsOpen] = useState(false);

  if (isExpandActionCell) {
    return (
      <div className={"d-flex flex-justify-center"}>
        <LinkButton
          data-test-id={"tabular-view-action-button-expand-details"}
          onClick={() => {
            setIsOpen(true);
          }}
          type={"button"}
        >
          {t("page.sa.housing.seemore")}
        </LinkButton>
        {isOpen && (
          <PurchasePlaceProvider value={"HSA"}>
            <PurchaseTypeProvider value={"hsa"}>
              <HSATabularViewModalProvider value={{ close: () => setIsOpen(false), open: () => setIsOpen(true) }}>
                <VariantProductsTableModal onClose={() => setIsOpen(false)} products={variantProducts} />
              </HSATabularViewModalProvider>
            </PurchaseTypeProvider>
          </PurchasePlaceProvider>
        )}
      </div>
    );
  }

  return <ProductTabularViewActionCell productCode={product.code} isHousing={true} />;
};

interface ModalContextValue {
  open: () => void;
  close: () => void;
}

const HSATabularViewModalContext = createContext<ModalContextValue>({
  open: () => {},
  close: () => {},
});

export const HSATabularViewModalProvider = HSATabularViewModalContext.Provider;

export const useHSATabularViewModalOpen = () => {
  const context = useContext(HSATabularViewModalContext);
  return context.open;
};
export const useHSATabularViewModalClose = () => {
  const context = useContext(HSATabularViewModalContext);
  return context.close;
};
